// src/pages/Sponsors.jsx
// Gestione Sponsor: caricamento loghi, riordino, attiva/disattiva, e pannello con
// gli URL degli overlay OBS (carosello / banner / ticker / singolo). I loghi
// caricati qui vengono usati dagli overlay /obs/sponsors* durante le gare.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from '../components/icons';
import { withFormat, loadObsVertical, saveObsVertical } from '../utils/obsFormat';
import './Sponsors.css';

const API_BASE = process.env.REACT_APP_API_BASE || (window.location.protocol === 'https:' ? '' : `http://${window.location.hostname}:5000`);
const assetUrl = (u) => (!u ? '' : (/^https?:\/\//i.test(u) ? u : `${API_BASE}${u}`));
const ORIGIN = typeof window !== 'undefined' ? window.location.origin : '';

const OVERLAY_VARIANTS = [
  { key: 'carousel', label: 'Carosello', path: '/obs/sponsors', desc: 'Tutti gli sponsor a rotazione con dissolvenza. A centro schermo.' },
  { key: 'banner', label: 'Banner', path: '/obs/sponsors/banner', desc: 'Tutti i loghi affiancati in una barra. Ideale in basso.' },
  { key: 'ticker', label: 'Ticker', path: '/obs/sponsors/ticker', desc: 'I loghi scorrono in continuo. Striscia inferiore sempre attiva.' },
  { key: 'flash', label: 'Flash', path: '/obs/sponsors/flash', desc: 'Uno sponsor appare per qualche secondo e scompare, a rotazione ogni pochi minuti. Parametri: ?show=7&every=180&pos=bl (bl/br/tl/tr/bc/tc).' },
];

// Icone locali (stroke, currentColor) non presenti nel set globale.
const svgProps = { width: 16, height: 16, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.9, strokeLinecap: 'round', strokeLinejoin: 'round', 'aria-hidden': true };
const IcCopy = () => (<svg {...svgProps}><rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>);
const IcExternal = () => (<svg {...svgProps}><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" /></svg>);
const IcTrash = () => (<svg {...svgProps}><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" /></svg>);
const IcEdit = () => (<svg {...svgProps}><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" /></svg>);
const IcUp = () => (<svg {...svgProps}><polyline points="18 15 12 9 6 15" /></svg>);
const IcDown = () => (<svg {...svgProps}><polyline points="6 9 12 15 18 9" /></svg>);
const IcImage = () => (<svg {...svgProps}><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><path d="M21 15l-5-5L5 21" /></svg>);

export default function Sponsors() {
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  // form nuovo sponsor
  const [name, setName] = useState('');
  const [website, setWebsite] = useState('');
  const [file, setFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const fileInputRef = useRef(null);
  const nameInputRef = useRef(null);

  // stato edit inline
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editWebsite, setEditWebsite] = useState('');
  const [editFile, setEditFile] = useState(null);

  const [copied, setCopied] = useState('');
  // Formato verticale 9:16 (dirette Instagram / YouTube), stessa scelta del pannello gara
  const [vertical, setVertical] = useState(loadObsVertical);
  const toggleVertical = (key) => setVertical(prev => {
    const next = { ...prev, [key]: !prev[key] };
    saveObsVertical(next);
    return next;
  });
  const singleUrl = (id) => withFormat(`${ORIGIN}/obs/sponsor/${id}`, !!vertical.sponsor);

  const load = useCallback(() => {
    setLoading(true);
    fetch(`${API_BASE}/api/sponsors`)
      .then(r => r.ok ? r.json() : Promise.reject(new Error('Errore caricamento')))
      .then(j => { setSponsors(j?.sponsors || []); setError(null); })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const onPickFile = (f, isEdit = false) => {
    if (isEdit) { setEditFile(f); return; }
    setFile(f);
    if (f) {
      const url = URL.createObjectURL(f);
      setFilePreview(url);
    } else {
      setFilePreview(null);
    }
  };

  const resetForm = () => {
    setName(''); setWebsite(''); setFile(null);
    if (filePreview) URL.revokeObjectURL(filePreview);
    setFilePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const addSponsor = async (e) => {
    e.preventDefault();
    if (!name.trim() || !file) { setError('Nome e logo sono obbligatori.'); return; }
    setBusy(true); setError(null);
    try {
      const fd = new FormData();
      fd.append('name', name.trim());
      if (website.trim()) fd.append('website', website.trim());
      fd.append('logo', file);
      const r = await fetch(`${API_BASE}/api/sponsors`, { method: 'POST', body: fd });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j?.error || 'Errore salvataggio');
      resetForm();
      load();
    } catch (e2) { setError(e2.message); }
    finally { setBusy(false); }
  };

  const startEdit = (s) => { setEditId(s.id); setEditName(s.name || ''); setEditWebsite(s.website || ''); setEditFile(null); };
  const cancelEdit = () => { setEditId(null); setEditFile(null); };

  const saveEdit = async (s) => {
    setBusy(true); setError(null);
    try {
      const fd = new FormData();
      fd.append('name', editName.trim());
      fd.append('website', editWebsite.trim());
      if (editFile) fd.append('logo', editFile);
      const r = await fetch(`${API_BASE}/api/sponsors/${s.id}`, { method: 'PUT', body: fd });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j?.error || 'Errore aggiornamento');
      cancelEdit();
      load();
    } catch (e2) { setError(e2.message); }
    finally { setBusy(false); }
  };

  const toggleActive = async (s) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('active', String(!(s.active !== false)));
      const r = await fetch(`${API_BASE}/api/sponsors/${s.id}`, { method: 'PUT', body: fd });
      if (!r.ok) throw new Error();
      load();
    } catch { setError('Errore aggiornamento stato'); }
    finally { setBusy(false); }
  };

  const move = async (idx, dir) => {
    const arr = [...sponsors];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    setSponsors(arr); // ottimistico
    setBusy(true);
    try {
      const r = await fetch(`${API_BASE}/api/sponsors/reorder`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orderedIds: arr.map(s => s.id) }),
      });
      if (!r.ok) throw new Error();
    } catch { setError('Errore riordino'); load(); }
    finally { setBusy(false); }
  };

  const removeSponsor = async (s) => {
    if (!window.confirm(`Eliminare lo sponsor "${s.name}"?`)) return;
    setBusy(true);
    try {
      const r = await fetch(`${API_BASE}/api/sponsors/${s.id}`, { method: 'DELETE' });
      if (!r.ok) throw new Error();
      load();
    } catch { setError('Errore eliminazione'); }
    finally { setBusy(false); }
  };

  const copy = (text, id) => {
    try {
      navigator.clipboard.writeText(text);
      setCopied(id);
      setTimeout(() => setCopied(''), 1600);
    } catch { /* clipboard non disponibile */ }
  };

  // Solo presentazione: porta il fuoco sul form "Nuovo sponsor".
  const focusForm = () => {
    nameInputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    nameInputRef.current?.focus({ preventScroll: true });
  };

  const activeCount = useMemo(() => sponsors.filter(s => s.active !== false).length, [sponsors]);

  return (
    <div className="rs-page spa-page">
      <header className="rs-page-header">
        <div className="rs-page-header__text">
          <span className="rs-kicker rs-kicker--red"><span className="rs-kicker__dot" />Gestione</span>
          <h1 className="rs-page-header__title">Sponsor</h1>
          <p className="rs-page-header__subtitle">
            Loghi partner e overlay OBS per la diretta · <span className="rs-num spa-count">{activeCount}</span> attivi su {sponsors.length}
          </p>
        </div>
        <div className="rs-page-header__actions">
          <button type="button" className="rs-btn rs-btn--primary" onClick={focusForm}>
            <Icon.Plus /> Nuovo sponsor
          </button>
        </div>
      </header>

      {error && (
        <div className="rs-alert rs-alert--danger spa-alert" role="alert">
          <Icon.Alert /><span>{error}</span>
        </div>
      )}

      <div className="spa-layout">
        {/* COLONNA SINISTRA: form + lista */}
        <div className="rs-stack spa-col">
          {/* FORM NUOVO */}
          <section className="rs-card" id="spa-new">
            <div className="rs-card__head">
              <span className="rs-card__title"><Icon.Plus /> Nuovo sponsor</span>
              <span className="rs-help">PNG / SVG / WEBP trasparente · max 5 MB</span>
            </div>
            <form onSubmit={addSponsor} className="rs-card__body spa-form">
              <div className="rs-grid rs-grid--2">
                <label className="rs-field">
                  <span className="rs-label">Nome</span>
                  <input ref={nameInputRef} className="rs-input" type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Es. Pirelli" maxLength={80} />
                </label>
                <label className="rs-field">
                  <span className="rs-label">Sito web <span className="rs-label__opt">opzionale</span></span>
                  <input className="rs-input" type="url" value={website} onChange={e => setWebsite(e.target.value)} placeholder="https://..." />
                </label>
              </div>
              <div className="rs-field">
                <span className="rs-label">Logo</span>
                <div
                  className={`rs-dropzone spa-dropzone ${filePreview ? 'has-file' : ''}`}
                  onClick={() => fileInputRef.current?.click()}
                  role="button"
                  tabIndex={0}
                  onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fileInputRef.current?.click(); } }}
                >
                  {filePreview ? (
                    <>
                      <span className="spa-logo-tile spa-logo-tile--lg"><img src={filePreview} alt="anteprima" /></span>
                      <strong>{file?.name}</strong>
                      <small>Clicca per cambiare file</small>
                    </>
                  ) : (
                    <>
                      <IcImage />
                      <strong>Scegli un file</strong>
                      <small>Il logo viene mostrato su fondo bianco negli overlay</small>
                    </>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml"
                    onChange={e => onPickFile(e.target.files?.[0] || null)}
                    hidden
                  />
                </div>
              </div>
              <div className="spa-form-actions">
                <button type="submit" className="rs-btn rs-btn--primary" disabled={busy}>
                  {busy ? <><span className="rs-spinner spa-spinner" /> Salvataggio…</> : <><Icon.Check /> Aggiungi sponsor</>}
                </button>
              </div>
            </form>
          </section>

          {/* LISTA */}
          <section className="rs-card">
            <div className="rs-card__head">
              <span className="rs-card__title"><Icon.Star /> Sponsor caricati</span>
              <span className="rs-badge">{sponsors.length} totali</span>
            </div>
            <div className="rs-card__body rs-card__body--flush">
              {loading ? (
                <div className="rs-empty spa-empty"><span className="rs-spinner" /><strong>Caricamento…</strong></div>
              ) : sponsors.length === 0 ? (
                <div className="rs-empty spa-empty">
                  <Icon.Star />
                  <strong>Nessuno sponsor</strong>
                  <p>Aggiungine uno per farlo comparire negli overlay OBS.</p>
                </div>
              ) : (
                <div className="rs-table-wrap spa-table-wrap">
                  <table className="rs-table spa-table">
                    <thead>
                      <tr>
                        <th className="spa-th-logo">Logo</th>
                        <th>Sponsor</th>
                        <th className="rs-hide-mobile">URL singolo</th>
                        <th className="spa-th-center">Attivo</th>
                        <th className="spa-th-center">Ordine</th>
                        <th className="spa-th-right">Azioni</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sponsors.map((s, idx) => (
                        editId === s.id ? (
                          <tr key={s.id} className="spa-row spa-row--edit">
                            <td className="spa-td-logo">
                              <span className="spa-logo-tile"><img src={assetUrl(s.logoUrl)} alt={s.name} /></span>
                            </td>
                            <td colSpan={5}>
                              <div className="spa-edit">
                                <div className="rs-grid rs-grid--2 spa-edit-grid">
                                  <label className="rs-field">
                                    <span className="rs-label">Nome</span>
                                    <input className="rs-input" value={editName} onChange={e => setEditName(e.target.value)} placeholder="Nome" />
                                  </label>
                                  <label className="rs-field">
                                    <span className="rs-label">Sito web</span>
                                    <input className="rs-input" value={editWebsite} onChange={e => setEditWebsite(e.target.value)} placeholder="https://..." />
                                  </label>
                                </div>
                                <div className="spa-edit-foot">
                                  <label className="rs-btn rs-btn--outline rs-btn--sm spa-replace">
                                    <input type="file" accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml"
                                      onChange={e => onPickFile(e.target.files?.[0] || null, true)} hidden />
                                    <IcImage />
                                    <span className="rs-truncate">{editFile ? editFile.name : 'Sostituisci logo…'}</span>
                                  </label>
                                  <span className="rs-spacer" />
                                  <button type="button" className="rs-btn rs-btn--ghost rs-btn--sm" onClick={cancelEdit}>Annulla</button>
                                  <button type="button" className="rs-btn rs-btn--primary rs-btn--sm" onClick={() => saveEdit(s)} disabled={busy}><Icon.Check /> Salva</button>
                                </div>
                              </div>
                            </td>
                          </tr>
                        ) : (
                          <tr key={s.id} className={`spa-row ${s.active === false ? 'is-off' : ''}`}>
                            <td className="spa-td-logo">
                              <span className="spa-logo-tile"><img src={assetUrl(s.logoUrl)} alt={s.name} /></span>
                            </td>
                            <td>
                              <div className="spa-name">
                                <strong>{s.name}</strong>
                                {s.website
                                  ? <a href={s.website} target="_blank" rel="noreferrer" className="spa-site rs-truncate">{s.website}</a>
                                  : <span className="spa-site spa-site--none">Nessun sito</span>}
                              </div>
                            </td>
                            <td className="rs-hide-mobile">
                              <div className="spa-single">
                                <code className="spa-code">/obs/sponsor/{s.id}{vertical.sponsor ? '?format=vertical' : ''}</code>
                                <button
                                  type="button"
                                  className={`rs-btn rs-btn--ghost rs-btn--sm rs-btn--icon ${copied === `s-${s.id}` ? 'is-copied' : ''}`}
                                  title={copied === `s-${s.id}` ? 'Copiato' : 'Copia URL singolo'}
                                  onClick={() => copy(singleUrl(s.id), `s-${s.id}`)}
                                >
                                  {copied === `s-${s.id}` ? <Icon.Check /> : <IcCopy />}
                                </button>
                              </div>
                            </td>
                            <td className="spa-td-center">
                              <button
                                type="button"
                                className={`rs-switch ${s.active === false ? '' : 'is-on'}`}
                                onClick={() => toggleActive(s)}
                                title={s.active === false ? 'Disattivato' : 'Attivo'}
                                aria-pressed={s.active !== false}
                                aria-label={s.active === false ? 'Attiva sponsor' : 'Disattiva sponsor'}
                              />
                            </td>
                            <td className="spa-td-center">
                              <div className="spa-order">
                                <button type="button" className="rs-btn rs-btn--outline rs-btn--sm rs-btn--icon" title="Sposta su" onClick={() => move(idx, -1)} disabled={idx === 0}><IcUp /></button>
                                <span className="spa-order-num rs-num">{String(idx + 1).padStart(2, '0')}</span>
                                <button type="button" className="rs-btn rs-btn--outline rs-btn--sm rs-btn--icon" title="Sposta giù" onClick={() => move(idx, 1)} disabled={idx === sponsors.length - 1}><IcDown /></button>
                              </div>
                            </td>
                            <td className="spa-td-right">
                              <div className="spa-actions">
                                <button type="button" className="rs-btn rs-btn--outline rs-btn--sm" onClick={() => startEdit(s)}><IcEdit /> <span className="rs-hide-mobile">Modifica</span></button>
                                <button type="button" className="rs-btn rs-btn--danger rs-btn--sm rs-btn--icon" title="Elimina" onClick={() => removeSponsor(s)}><IcTrash /></button>
                              </div>
                            </td>
                          </tr>
                        )
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </section>
        </div>

        {/* COLONNA DESTRA: overlay OBS + anteprima */}
        <div className="rs-stack spa-col">
          <section className="rs-card rs-card--accent">
            <div className="rs-card__head">
              <span className="rs-card__title"><Icon.Monitor /> Overlay OBS</span>
              <span className="rs-badge rs-badge--lime">Browser source</span>
            </div>
            <div className="rs-card__body">
              <p className="spa-obs-intro">
                Aggiungi questi URL come <strong>Browser Source</strong> in OBS con il filtro <strong>Chroma Key</strong> (sfondo verde <code>#00ff00</code>, come gli altri overlay di gara).
              </p>
              <div className="spa-obs-list">
                {OVERLAY_VARIANTS.map(v => {
                  const fmtKey = v.path.replace(/^\/obs\//, '');
                  const url = withFormat(`${ORIGIN}${v.path}`, !!vertical[fmtKey]);
                  return (
                    <div className="spa-obs-item" key={v.key}>
                      <div className="spa-obs-head">
                        <span className="spa-obs-label">{v.label}</span>
                        <span className="spa-obs-desc">{v.desc}</span>
                      </div>
                      <label className="rs-check spa-obs-fmt">
                        <input type="checkbox" checked={!!vertical[fmtKey]} onChange={() => toggleVertical(fmtKey)} />
                        Verticale 9:16 · dirette Instagram / YouTube
                      </label>
                      <div className="rs-input-group">
                        <input className="rs-input rs-input--mono spa-obs-url" value={url} readOnly onFocus={e => e.target.select()} aria-label={`URL overlay ${v.label}`} />
                        <button type="button" className={`rs-btn spa-obs-btn ${copied === `v-${v.key}` ? 'is-copied' : ''}`} onClick={() => copy(url, `v-${v.key}`)} title="Copia URL">
                          {copied === `v-${v.key}` ? <><Icon.Check /> Copiato</> : <><IcCopy /> Copia</>}
                        </button>
                        <button type="button" className="rs-btn rs-btn--icon spa-obs-btn" onClick={() => window.open(url, '_blank', 'noopener,noreferrer')} title="Apri in una nuova scheda">
                          <IcExternal />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
              <label className="rs-check spa-obs-fmt spa-obs-fmt--single">
                <input type="checkbox" checked={!!vertical.sponsor} onChange={() => toggleVertical('sponsor')} />
                URL dei singoli sponsor (tabella sopra) in verticale 9:16
              </label>
              <p className="spa-tip">
                In verticale usa in OBS una Browser Source <strong>1080×1920</strong>: la grafica è compatta e sta nella fascia bassa dello schermo, sopra i commenti, così non copre la diretta (<code>?band=260</code> altezza della fascia · <code>?pos=tc</code> in alto · <code>?safe=0</code> tutto lo schermo).
              </p>
              <p className="spa-tip">
                Parametri opzionali: <code>?interval=6</code> secondi carosello · <code>?speed=32</code> velocità ticker · <code>?plate=0</code> solo logo · <code>?bg=transparent</code> sfondo trasparente invece del verde.
              </p>
            </div>
          </section>

          <section className="rs-card">
            <div className="rs-card__head">
              <span className="rs-card__title"><Icon.Eye /> Anteprima carosello</span>
              <span className="rs-help rs-mono">16:9 · bg=dark</span>
            </div>
            <div className="rs-card__body rs-card__body--flush">
              <div className="spa-preview">
                <iframe title="Anteprima sponsor" src={`${ORIGIN}/obs/sponsors?bg=dark`} className="spa-preview-frame" />
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
